import { useMutation, useQuery } from '@apollo/client'
import { useToast } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { endOfMonth, startOfMonth } from 'date-fns'
import { ExportFormat, ExportOption } from '@/modules/exports'
import {
  EXPORT_DRIVER_GRADES,
  GENERATE_TAX_ZONE_REPORT,
} from '@/modules/exports/mutations'
import { useInfringementsMutations } from '@/modules/infringements'
import { useCallback, useMemo } from 'react'
import { useOnErrorAuto, useUser } from '@/utils/hooks'
import { Module } from '..'
import { ALL_MODULE_CONFIG_QUERYS_SHALLOW } from '../queries'
import type { InfringementsSettingsForm } from './util/InfringementsSettings'

export type ActiveModuleName = keyof ReturnType<
  typeof useModuleConfigs
>['active']

export function useModuleConfigs(skip: boolean = false) {
  const { data, loading, refetch } = useQuery(
    ALL_MODULE_CONFIG_QUERYS_SHALLOW,
    { skip }
  )

  const modules = useMemo(
    () => data?.allModuleConfigs ?? [],
    [data?.allModuleConfigs]
  )

  const moduleNameModules = useMemo<Record<string, (typeof modules)[number]>>(
    () => Object.fromEntries(modules.map(module => [module.name, module])),
    [modules]
  )

  const moduleNames = useMemo(
    () => modules.map(module => module.name),
    [modules]
  )

  const activeModuleNames = useMemo(
    () => modules.filter(module => module.active).map(module => module.name),
    [modules]
  )

  const active = useMemo(
    () => ({
      activities: activeModuleNames.includes(Module.ACTIVITIES),
      terminal: activeModuleNames.includes(Module.TERMINAL),
      timesheets: activeModuleNames.includes(Module.TIMESHEETS),
      vehicles: activeModuleNames.includes(Module.VEHICLES),
      absences: activeModuleNames.includes(Module.ABSENCES),
      handbook: activeModuleNames.includes(Module.HANDBOOK),
      forum: activeModuleNames.includes(Module.FORUM),
      issues: activeModuleNames.includes(Module.ISSUES),
      infringements: activeModuleNames.includes(Module.INFRINGEMENTS),
      workInfringements: activeModuleNames.includes(
        Module.WORK_DAY_INFRINGEMENTS
      ),
      routePlanner: activeModuleNames.includes(Module.ROUTEPLANNER),
      fleet: activeModuleNames.includes(Module.FLEET),
      driverPlanner: activeModuleNames.includes(Module.PLANNER),
      salary: activeModuleNames.includes(Module.SALARY),
      learning: activeModuleNames.includes(Module.LEARNING),
    }),
    [activeModuleNames]
  )

  const hasAnyModule = useMemo(
    () => Object.values(active).some(v => v),
    [active]
  )

  return {
    modules,
    moduleNameModules,
    moduleNames,

    loading,

    active,
    hasAnyModule,

    refetch,
  }
}

export function useCompositeModules() {
  const { active } = useModuleConfigs()

  const dashboardStatisticsModuleActive = active.activities || active.issues

  const activitiesHandbookIssuesActive =
    active.activities || active.handbook || active.issues

  const mainModuleActive =
    active.activities ||
    active.terminal ||
    active.timesheets ||
    active.absences ||
    active.issues ||
    active.infringements ||
    active.workInfringements

  return {
    dashboardStatisticsModuleActive,
    activitiesHandbookIssuesActive,
    mainModuleActive,
  }
}

export interface DateRangeForm {
  dateStart: Date
  dateEnd: Date
}

export function useExportInfringementsForm(onCompleted?: () => void) {
  const translations = useTranslate({
    selectUsersError: 'activities.errors.select-multiple-users',
  })

  const me = useUser()

  const {
    exportInfringementsMass,
    exportInfringements,
    loading: mailLoading,
  } = useInfringementsMutations(onCompleted)

  const {
    formValues: form,
    formErrors: errors,
    updateForm,
    submitHandler: submit,
  } = useForm<InfringementsSettingsForm>({
    values: {
      month: new Date(),
      dateStart: new Date(),
      dateEnd: new Date(),
      useDateRange: false,
      exportOption: ExportOption.SAVE,
      exportFormat: ExportFormat.PDF,
      exportMass: false,
      user: '',
    },
    validators: {
      user: (value, form) =>
        !(value || form.exportMass) ? translations.selectUsersError : null,
    },
  })

  const handler = useCallback(
    <K extends keyof InfringementsSettingsForm>(key: K) => {
      return (value: InfringementsSettingsForm[K] | null) => {
        if (value === null) return
        updateForm({ [key]: value })
      }
    },
    [updateForm]
  )

  const handleExport = useCallback(
    async (values: InfringementsSettingsForm) => {
      const dateStart = form.useDateRange
        ? form.dateStart
        : startOfMonth(values.month)
      const dateEnd = form.useDateRange
        ? form.dateEnd
        : endOfMonth(values.month)

      if (form.user) {
        void exportInfringements({
          variables: {
            userMail: me.id,
            dateStart: dateStart.toISOString(),
            dateEnd: dateEnd.toISOString(),
            user: form.user,
            exportOption: values.exportOption,
          },
        })
      } else {
        void exportInfringementsMass({
          variables: {
            userMail: me.id,
            dateStart: dateStart.toISOString(),
            dateEnd: dateEnd.toISOString(),
            exportOption: values.exportOption,
            exportFormat: values.exportFormat,
          },
        })
      }
    },
    [
      exportInfringements,
      exportInfringementsMass,
      form.dateEnd,
      form.dateStart,
      form.useDateRange,
      form.user,
      me.id,
    ]
  )

  return {
    form,
    errors,
    loading: mailLoading,
    submit,
    updateForm,
    handleExport,
    handler,
  }
}

export interface DriverGradesExportForm {
  month: Date
  dateStart: Date
  dateEnd: Date
  useDateRange: boolean
  exportOption: ExportOption
  sources: ('VOLVO' | 'SCANIA')[]
  sortBy: 'NAME' | 'AVG_DESC'
}

export const useExportDriverGradesForm = (onCompleted?: () => void) => {
  const me = useUser()
  const addToast = useToast()
  const onErrorAuto = useOnErrorAuto()

  const translations = useTranslate({
    success: 'exports.success-user-exports',
    exportTimeout: 'exports.export-timeout',
    error: 'exports.error-email-process',
  })

  const [exportDriverGrades, { loading }] = useMutation(EXPORT_DRIVER_GRADES, {
    onCompleted(data) {
      if (data?.exportDriverGrades?.exportTimeout) {
        addToast('info', translations.exportTimeout)
      } else if (data?.exportDriverGrades?.ok) {
        addToast('info', translations.success)
      }
      onCompleted?.()
    },
    onError: onErrorAuto(translations.error),
  })

  const {
    formValues: form,
    formErrors: errors,
    updateForm,
    submitHandler: submit,
  } = useForm<DriverGradesExportForm>({
    values: {
      month: new Date(),
      dateStart: startOfMonth(new Date()),
      dateEnd: endOfMonth(new Date()),
      useDateRange: false,
      exportOption: ExportOption.SAVE,
      sources: [],
      sortBy: 'NAME',
    },
  })

  const handler = useCallback(
    <K extends keyof DriverGradesExportForm>(key: K) => {
      return (value: DriverGradesExportForm[K] | null) => {
        if (value === null) return
        updateForm({ [key]: value })
      }
    },
    [updateForm]
  )

  const handleExport = useCallback(
    async (values: DriverGradesExportForm) => {
      const dateStart = values.useDateRange
        ? values.dateStart
        : startOfMonth(values.month)
      const dateEnd = values.useDateRange
        ? values.dateEnd
        : endOfMonth(values.month)

      void exportDriverGrades({
        variables: {
          userMail: me.id,
          dateStart: dateStart.toISOString(),
          dateEnd: dateEnd.toISOString(),
          exportOption: values.exportOption,
          exportFormat: ExportFormat.EXCEL,
          sources: values.sources.length ? values.sources : null,
          sortBy: values.sortBy,
        },
      })
    },
    [exportDriverGrades, me.id]
  )

  return {
    form,
    errors,
    loading,
    submit,
    updateForm,
    handler,
    handleExport,
  }
}

export const useSalaryModuleTranslations = () => {
  const translations = useTranslate({
    activities: 'common.activities',
    terminal: 'common.terminal',
    timesheets: 'common.timesheets',
  })

  function getModuleTranslation(moduleName: string) {
    switch (moduleName) {
      case Module.ACTIVITIES:
        return translations.activities
      case Module.TERMINAL:
        return translations.terminal
      case Module.TIMESHEETS:
        return translations.timesheets
      default:
        return ''
    }
  }

  return getModuleTranslation
}

export type TaxZoneReportType = 'TAX_ZONES' | 'COUNTRY_AND_TAX_ZONES'

interface TaxZoneReportForm {
  reportType: TaxZoneReportType
  dateStart: Date
  dateEnd: Date
  // Empty = all active drivers ("select all").
  userIds: string[]
}

export const useExportTaxZoneReportForm = (onCompleted?: () => void) => {
  const addToast = useToast()
  const onErrorAuto = useOnErrorAuto()

  const translations = useTranslate({
    success: 'exports.success-user-exports',
    error: 'exports.error-email-process',
  })

  const [generateTaxZoneReport, { loading }] = useMutation(
    GENERATE_TAX_ZONE_REPORT,
    {
      onCompleted(data) {
        if (data?.generateTaxZoneReport?.success) {
          addToast('info', translations.success)
        }
        onCompleted?.()
      },
      onError: onErrorAuto(translations.error),
    }
  )

  const {
    formValues: form,
    formErrors: errors,
    updateForm,
    submitHandler: submit,
  } = useForm<TaxZoneReportForm>({
    values: {
      reportType: 'TAX_ZONES',
      dateStart: startOfMonth(new Date()),
      dateEnd: endOfMonth(new Date()),
      userIds: [],
    },
  })

  const handler = useCallback(
    <K extends keyof TaxZoneReportForm>(key: K) => {
      return (value: TaxZoneReportForm[K] | null) => {
        if (value === null) return
        updateForm({ [key]: value })
      }
    },
    [updateForm]
  )

  const handleExport = useCallback(
    async (values: TaxZoneReportForm) => {
      void generateTaxZoneReport({
        variables: {
          reportType: values.reportType,
          startDate: values.dateStart.toISOString(),
          endDate: values.dateEnd.toISOString(),
          // Empty selection = all active drivers.
          userIds: values.userIds.length ? values.userIds : null,
        },
      })
    },
    [generateTaxZoneReport]
  )

  return {
    form,
    errors,
    loading,
    submit,
    updateForm,
    handler,
    handleExport,
  }
}
